import { FC } from 'react'

import { Flex, HStack, Text } from '@chakra-ui/react'
import { InspectionItem, InspectionItemNumberValues } from '@editor/interfaces/inspection'
import { InspectionGroup } from '@editor/interfaces/inspectionGroup'
import { ProjectSheetToleranceType as ToleranceType } from '@editor/interfaces/project'
import { patchInspectionGroup } from '@editor/pages/projects/editor/store/inspectionGroup'
import { isInspectionGroup } from '@editor/services/InspectionGroup'
import { RootState, useAppDispatch } from '@editor/store/app'
import { getContrastingColor } from '@uiw/react-color'
import { useSelector } from 'react-redux'

import Metric from './Metric/Metric'
import PopoverEditingGroup from './PopoverEditingGroup'
import TableHeader from './TableHeader'

const MetricsGroup: FC<{
  /**
   * Inspection group for the metrics.
   */
  group?: InspectionGroup

  /**
   * Title of the metrics group
   */
  title: string

  /**
   * Function to get the default title of the inspection item.
   * @param item Current inspection item
   * @param index Index of the current inspection item
   */
  itemsDefaulTitle: (item: InspectionItem, index: number) => string

  /**
   * Unit of the values in the metrics group
   */
  unit: string

  /**
   * All inspection items in the metrics group
   */
  inspectionItems: InspectionItem[]

  /**
   * Values of the inspection items in the metrics.
   * Make sure that the order of the values matches the order of the inspection items.
   */
  inspectionItemValues: InspectionItemNumberValues[]

  /**
   * Whether the individual metrics are hidden or not.
   */
  isMetricsHidden: boolean

  /**
   * Whether the name of the inspection item is changeable or not.
   */
  nameChangeable?: boolean

  /**
   * All additional metrics' toggle state, including ones not belonging to this metric.
   */
  allAdditionalMetricsToggle?: boolean[]

  /**
   * Type of tolerance for the inspection item.
   */
  toleranceType: ToleranceType
}> = ({
  group,
  title,
  itemsDefaulTitle,
  inspectionItems,
  inspectionItemValues,
  unit,
  isMetricsHidden,
  nameChangeable,
  allAdditionalMetricsToggle,
  toleranceType,
}) => {
  // Store
  const dispatch = useAppDispatch()
  const groups = useSelector((state: RootState) => state.inspectionGroup.groups)

  return (
    <Flex
      alignItems="center"
      bg="primary.50"
      className="metrics-group data-rows"
      borderLeft={`12px solid ${group ? group.color : 'var(--chakra-colors-primary-50)'}`}
    >
      <HStack
        p={4}
        fontSize="xl"
        w="100%"
        gap={8}
        backgroundColor={group ? group.color : 'primary.50'}
        color={group ? getContrastingColor(group.color) : undefined}
      >
        {group ? (
          <PopoverEditingGroup
            group={group}
            onComplete={(name, color) => {
              const groupPanelItem = groups.find((g) => isInspectionGroup(g.item) && g.item.group_id === group.group_id)

              if (isInspectionGroup(groupPanelItem?.item)) {
                dispatch(
                  patchInspectionGroup({
                    ...groupPanelItem,
                    name,
                    color,
                    item: {
                      ...groupPanelItem.item,
                      name,
                      color,
                    },
                  }),
                )
              }
            }}
          />
        ) : null}
        <Text>{title}</Text>
      </HStack>

      <Flex w="100%" flexDirection="column">
        <TableHeader allAdditionalMetricsToggle={allAdditionalMetricsToggle} />
        <Flex flexDirection="column" bg="white" borderRightWidth={1} borderRightColor="gray2.300">
          {/* Metrics go here */}
          {inspectionItems.map((inspectionItem, index) => (
            <Metric
              key={`${inspectionItem.inspection_item_id}-metric`}
              inspectionItem={inspectionItem}
              title={inspectionItem.part_name}
              defaulTitle={itemsDefaulTitle(inspectionItem, index)}
              inspectionItemValue={inspectionItemValues[index]}
              unit={unit}
              isHidden={isMetricsHidden}
              nameChangeable={nameChangeable}
              allAdditionalMetricsToggle={allAdditionalMetricsToggle}
              toleranceType={toleranceType}
            />
          ))}
        </Flex>
      </Flex>
    </Flex>
  )
}

export default MetricsGroup
